import React from 'react';
import { useMeasure } from 'react-use';
import { AlertState, PluginContextProvider } from '@grafana/data';
import { getAppEvents } from '@grafana/runtime';
import { useStyles2, Tooltip, PanelChrome, Icon, ErrorBoundaryAlert, PanelContextProvider } from '@grafana/ui';
import { sceneGraph } from '../../core/sceneGraph/index.js';
import { isSceneObject } from '../../core/types.js';
import { css, cx } from '@emotion/css';

function VizPanelRenderer({ model }) {
  var _a, _b, _c;
  const {
    title,
    options,
    fieldConfig,
    _pluginLoadError,
    $data,
    displayMode,
    hoverHeader,
    menu,
    headerActions,
    titleItems,
    description
  } = model.useState();
  const [ref, { width, height }] = useMeasure();
  const plugin = model.getPlugin();
  const { dragClass, dragClassCancel } = getDragClasses(model);
  const dataObject = sceneGraph.getData(model);
  const rawData = dataObject.useState();
  const dataWithFieldConfig = model.applyFieldConfig(rawData.data);
  const sceneTimeRange = sceneGraph.getTimeRange(model);
  const timeZone = sceneTimeRange.getTimeZone();
  const timeRange = getPanelTimeRange(sceneTimeRange, plugin, dataWithFieldConfig);
  const titleInterpolated = model.interpolate(title, void 0, "text");
  const alertStateStyles = useStyles2(getAlertStateStyles);
  if (!plugin) {
    return /* @__PURE__ */ React.createElement("div", null, "Loading plugin panel...");
  }
  if (!plugin.panel) {
    return /* @__PURE__ */ React.createElement("div", null, "Panel plugin has no panel component");
  }
  const PanelComponent = plugin.panel;
  if ($data && $data.setContainerWidth) {
    $data.setContainerWidth(Math.round(width));
  }
  let titleItemsElement = [];
  if (titleItems) {
    if (Array.isArray(titleItems)) {
      titleItemsElement = titleItemsElement.concat(
        titleItems.map((titleItem) => {
          return /* @__PURE__ */ React.createElement(titleItem.Component, {
            model: titleItem,
            key: `${titleItem.state.key}`
          });
        })
      );
    } else if (isSceneObject(titleItems)) {
      titleItemsElement.push(/* @__PURE__ */ React.createElement(titleItems.Component, {
        model: titleItems
      }));
    } else {
      titleItemsElement.push(titleItems);
    }
  }
  if (model.state.$timeRange) {
    titleItemsElement.push(/* @__PURE__ */ React.createElement(model.state.$timeRange.Component, {
      model: model.state.$timeRange,
      key: model.state.key
    }));
  }
  if (dataWithFieldConfig.alertState) {
    titleItemsElement.push(
      /* @__PURE__ */ React.createElement(Tooltip, {
        content: (_a = dataWithFieldConfig.alertState.state) != null ? _a : "unknown",
        key: `alert-states-icon-${model.state.key}`
      }, /* @__PURE__ */ React.createElement(PanelChrome.TitleItem, {
        className: cx({
          [alertStateStyles.ok]: dataWithFieldConfig.alertState.state === AlertState.OK,
          [alertStateStyles.pending]: dataWithFieldConfig.alertState.state === AlertState.Pending,
          [alertStateStyles.alerting]: dataWithFieldConfig.alertState.state === AlertState.Alerting
        })
      }, /* @__PURE__ */ React.createElement(Icon, {
        name: dataWithFieldConfig.alertState.state === "alerting" ? "heart-break" : "heart",
        className: "panel-alert-icon",
        size: "md"
      })))
    );
  }
  let panelMenu;
  if (menu) {
    panelMenu = /* @__PURE__ */ React.createElement(menu.Component, {
      model: menu
    });
  }
  let actionsElement;
  if (headerActions) {
    if (Array.isArray(headerActions)) {
      actionsElement = /* @__PURE__ */ React.createElement(React.Fragment, null, headerActions.map((action) => {
        return /* @__PURE__ */ React.createElement(action.Component, {
          model: action,
          key: `${action.state.key}`
        });
      }));
    } else if (isSceneObject(headerActions)) {
      actionsElement = /* @__PURE__ */ React.createElement(headerActions.Component, {
        model: headerActions
      });
    } else {
      actionsElement = headerActions;
    }
  }
  const data = dataWithFieldConfig;
  const isReadyToRender = dataObject.isDataReadyToDisplay ? dataObject.isDataReadyToDisplay() : true;
  const context = model.getPanelContext();
  const panelId = (_c = (_b = context.instanceState) == null ? void 0 : _b.legacyPanelId) != null ? _c : 1;
  return /* @__PURE__ */ React.createElement("div", {
    className: relativeWrapper
  }, /* @__PURE__ */ React.createElement("div", {
    ref,
    className: absoluteWrapper,
    "data-viz-panel-key": model.state.key
  }, width > 0 && height > 0 && /* @__PURE__ */ React.createElement(PanelChrome, {
    title: titleInterpolated,
    description: (description == null ? void 0 : description.trim()) ? model.getDescription : void 0,
    loadingState: data.state,
    statusMessage: getChromeStatusMessage(data, _pluginLoadError),
    statusMessageOnClick: model.onStatusMessageClick,
    width,
    height,
    displayMode,
    hoverHeader,
    titleItems: titleItemsElement,
    dragClass,
    actions: actionsElement,
    dragClassCancel,
    padding: plugin.noPadding ? "none" : "md",
    menu: panelMenu,
    onCancelQuery: model.onCancelQuery
  }, (innerWidth, innerHeight) => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ErrorBoundaryAlert, {
    dependencies: [plugin, data]
  }, /* @__PURE__ */ React.createElement(PluginContextProvider, {
    meta: plugin.meta
  }, /* @__PURE__ */ React.createElement(PanelContextProvider, {
    value: context
  }, isReadyToRender && /* @__PURE__ */ React.createElement(PanelComponent, {
    id: panelId,
    data,
    title,
    timeRange,
    timeZone,
    options,
    fieldConfig,
    transparent: false,
    width: innerWidth,
    height: innerHeight,
    renderCounter: 0,
    replaceVariables: model.interpolate,
    onOptionsChange: model.onOptionsChange,
    onFieldConfigChange: model.onFieldConfigChange,
    onChangeTimeRange: model.onTimeRangeChange,
    eventBus: getAppEvents()
  }))))))));
}
function getDragClasses(panel) {
  var _a, _b;
  const parentLayout = sceneGraph.getLayout(panel);
  const isDraggable = parentLayout == null ? void 0 : parentLayout.isDraggable();
  if (!parentLayout || !isDraggable || itemDraggingDisabled(panel, parentLayout)) {
    return { dragClass: "", dragClassCancel: "" };
  }
  return { dragClass: (_a = parentLayout.getDragClass) == null ? void 0 : _a.call(parentLayout), dragClassCancel: (_b = parentLayout == null ? void 0 : parentLayout.getDragClassCancel) == null ? void 0 : _b.call(parentLayout) };
}
function itemDraggingDisabled(item, layout) {
  let ancestor = item.parent;
  while (ancestor && ancestor !== layout) {
    if ("isDraggable" in ancestor.state && ancestor.state.isDraggable === false) {
      return true;
    }
    ancestor = ancestor.parent;
  }
  return false;
}
function getChromeStatusMessage(data, pluginLoadingError) {
  if (pluginLoadingError) {
    return pluginLoadingError;
  }
  let message = data.error ? data.error.message : void 0;
  if (data.errors) {
    message = data.errors.map((e) => e.message).join(", ");
  }
  return message;
}
const relativeWrapper = css({
  position: "relative",
  width: "100%",
  height: "100%"
});
const absoluteWrapper = css({
  position: "absolute",
  width: "100%",
  height: "100%"
});
const getAlertStateStyles = (theme) => {
  return {
    ok: css({
      color: theme.colors.success.text
    }),
    pending: css({
      color: theme.colors.warning.text
    }),
    alerting: css({
      color: theme.colors.error.text
    })
  };
};
function getPanelTimeRange(sceneTimeRange, plugin, data) {
  var _a;
  if (!plugin || plugin.meta.skipDataQuery) {
    return sceneTimeRange.state.value;
  }
  if (data && ((_a = data.request) == null ? void 0 : _a.range)) {
    return data.request.range;
  }
  return sceneTimeRange.state.value;
}

export { VizPanelRenderer };
//# sourceMappingURL=VizPanelRenderer.js.map
